import { useState, useEffect, useCallback } from 'react';

const CLOUD_API = 'https://api.yoohuo.fun';
const TOKEN_KEY = 'lobster_jwt';
const USER_KEY = 'lobster_user';

interface User {
  id: string;
  username: string;
}

export function useCloudAuth() {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  // Verify stored token on mount
  useEffect(() => {
    async function verify() {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) { setLoading(false); return; }

      try {
        const res = await fetch(`${CLOUD_API}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const u = { id: data.user.id, username: data.user.username || data.user.email };
          setUser(u);
          localStorage.setItem(USER_KEY, JSON.stringify(u));
        } else {
          // Token expired
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setUser(null);
        }
      } catch {
        // Offline — trust cached user
      }
      setLoading(false);
    }
    verify();
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<string | null> => {
    const res = await fetch(`${CLOUD_API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json();

    if (res.ok && data.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
      const u = { id: data.user.id, username: data.user.username };
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      setUser(u);
      return null; // no error
    }
    return data.error || 'Login failed';
  }, []);

  const register = useCallback(async (username: string, password: string): Promise<string | null> => {
    const res = await fetch(`${CLOUD_API}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json();

    if (res.ok && data.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
      const u = { id: data.user.id, username: data.user.username };
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      setUser(u);
      return null;
    }
    return data.error || 'Registration failed';
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const getToken = useCallback(() => localStorage.getItem(TOKEN_KEY), []);

  return { user, loading, login, register, logout, getToken };
}
